/**
 * Payment Method Templates Store - Zustand State Management
 * 
 * Manages available payment method templates state for users.
 * Allows users to view and add templates they don't currently have.
 * 
 * @validates Requirements 6.1-6.5 - Template availability and re-addition
 */

import { create } from 'zustand';
import { PaymentMethodTemplate, PaymentMethod } from '@budgira/shared';
import { paymentMethodService } from '../services/paymentMethod.service';
import i18n from '../i18n';

interface PaymentMethodTemplatesState {
  templates: PaymentMethodTemplate[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAvailableTemplates: () => Promise<void>;
  addTemplate: (templateId: string) => Promise<PaymentMethod | null>;
  clearError: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeTemplateError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    'TEMPLATE_NOT_FOUND': 'paymentMethodTemplates.errors.notFound',
    'TEMPLATE_ALREADY_ADDED': 'paymentMethodTemplates.errors.alreadyAdded',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('paymentMethodTemplates.errors.generic'));
}

export const usePaymentMethodTemplatesStore = create<PaymentMethodTemplatesState>((set, get) => ({
  templates: [],
  isLoading: false,
  error: null,

  /**
   * Fetch available templates for the current user
   * @validates Requirement 6.1, 6.2
   */
  fetchAvailableTemplates: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.getAvailableTemplates();
      if (response.success && response.data) {
        set({ templates: response.data, isLoading: false });
      } else {
        set({ 
          error: response.error?.message || String(i18n.t('paymentMethodTemplates.errors.generic')), 
          isLoading: false 
        });
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Add a template to the user's payment methods
   * @validates Requirement 6.4
   */
  addTemplate: async (templateId: string): Promise<PaymentMethod | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await paymentMethodService.addTemplate(templateId);
      if (response.success && response.data) {
        // Refresh available templates to update the list
        await get().fetchAvailableTemplates();
        return response.data;
      } else {
        set({ 
          error: response.error?.message || String(i18n.t('paymentMethodTemplates.errors.generic')), 
          isLoading: false 
        });
        return null;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  clearError: () => set({ error: null }),
}));

export default usePaymentMethodTemplatesStore;
